<template>
    <basic-header pretitle="aqfer Marketing Data Platform" title="Analytics Engine Demo" :compact="true" />
    <div class="container-fluid">
        <div class="row mt-5">
            <div class="d-flex justify-content-between align-items-center w-100">
                <div class="d-flex align-items-center">
                    <div class="me-2">
                        <input-field colField="mb-0" :editable="true" label="Audience" :options="audienceOptions"
                            type="select" v-model="audience" style="width:150px" />
                    </div>
                    <div class="me-2">
                        <input-field colField="mb-0" :editable="true" label="Data Environment" type="text"
                            v-model="dataEnvironment" style="width:150px" />
                    </div>
                    <div class="me-2">
                        <input-field colField="mb-0" :editable="true" label="Dataset" type="text" v-model="dataset"
                            style="width:200px" />
                    </div>
                    <div class="attributes-input-wrapper">
                        <div v-if="attributesMultiselect">
                            <input-field colField="mb-0" label="Select Attributes" type="multiselect"
                                v-model="attributes" style="width:300px" :toolTip="'Select attributes in the dropdown'"
                                :options="attributesOptions" />
                        </div>
                        <div v-else>
                            <input-field colField="mb-0" label="Select Attributes" type="text" v-model="attributesText"
                                style="width:300px" :toolTip="'Copy paste comma separated attributes'"
                                :placeholder="'Comma separated attributes'" />
                        </div>
                    </div>
                    <div class="mt-2 me-2 input-group attributes-buttons">
                        <button class="btn btn-warning mt-4" style="height:39px"
                            @click="attributesMultiselect = !attributesMultiselect">
                            <span v-if="attributesMultiselect" class="fa fa-list"></span>
                            <span v-else class="fa fa-paste"></span>
                        </button>
                        <button class="btn btn-primary mt-4" @click="getDataset()"
                            :disabled="!dataset || !dataEnvironment || attrLoading" style="height:39px">
                            <div class="spinner-border spinner-border-sm" role="status" v-if="attrLoading">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <div v-else>
                                <span class="fa fa-refresh"></span>
                            </div>
                        </button>
                    </div>
                    <div class="mt-2">
                        <button class="btn btn-secondary mt-4" @click="viewCriterion()" v-if="finalCriterion" :style="{ width: '123px' }">
                            View Criterion
                        </button>
                    </div>
                </div>
                <div class="d-flex align-items-center gap-3 pt-1">
                    <button class="btn btn-success mt-4" @click="compareResults(false)" :disabled="loading || !dataEnvironment || !dataset || !audience">
                        <div class="spinner-border spinner-border-sm" role="status" v-if="loading">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <div v-else>
                            Get Histogram
                        </div>
                    </button>
                    <button class="btn btn-secondary mt-4" v-if="loading" @click="stopPolling()">
                        Cancel
                    </button>
                </div>
            </div>
        </div>
        <div class="row justify-content-end">
            <div class="col-sm-3 d-flex position-absolute mt-3 pt-1 justify-content-end">
                <input-field label="Skip Cache" type="checkbox" :toolTip="'Check this to force a new query execution'"
                    v-model="skipCache" />
            </div>
        </div>
        <Tabs :tabs="tabItems">
            <template v-slot:stats>
                <div class="row mt-3">
                    <div v-if="isResultObtained && !loading">
                        <div class="card card-body">
                            <div class="d-flex justify-content-center mb-2">
                                <span class="fw-bold">Comparison</span>
                            </div>
                            <div class="row">
                                <div v-for="(item, index) in comparisonBarData" :key="index" class="col-6 p-3">
                                    <Bar :data="item" :options="chartOptions" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-6">
                        <div v-if="!histogramAudsumLoading && !histogramAudsumResult">
                            <div class="card card-body">
                                <span class="text-center">No data found!</span>
                            </div>
                        </div>
                        <div v-else class="card">
                            <div class="card-header">
                                <div class="d-flex justify-content-center">
                                    <span class="fw-bold">Athena Execution Details</span>
                                </div>
                            </div>
                            <div class="card-body">
                                <div v-if="histogramAudsumLoading">
                                    <div class="d-flex justify-content-center align-items-center">
                                        <div class="spinner-border spinner-border-sm" role="status">
                                            <span class="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                    <div class="d-flex justify-content-center align-items-center">
                                        <p>{{ (timer / 1000).toFixed(3) }}s</p>
                                    </div>
                                </div>
                                <div class="list-group list-group-flush my-n3">
                                    <div class="list-group-item" v-if="histogramAudsumResult?.execution_id">
                                        <div class="row align-items-center">
                                            <div class="col">
                                                <span class="mb-0">Execution ID</span>
                                            </div>
                                            <div class="col-auto">
                                                <span>
                                                    {{ histogramAudsumResult.execution_id }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="list-group-item" v-if="histogramAudsumResult?.status">
                                        <div class="row align-items-center rounded" style="height: 40px;"
                                            :style="{ backgroundColor: getStatusColor(histogramAudsumResult.status) }">
                                            <div class="col">
                                                <span class="mb-0">Status</span>
                                            </div>
                                            <div class="col-auto">
                                                <strong class="text-uppercase">
                                                    <div class="spinner-border spinner-border-sm" role="status"
                                                        v-if="histogramAudsumResult.status.toLowerCase() == 'running'">
                                                        <span class="visually-hidden">Loading...</span>
                                                    </div>
                                                    {{ histogramAudsumResult.status }}
                                                </strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="list-group-item"
                                        v-if="histogramAudsumResult?.statistics.execution_time_millis">
                                        <div class="row align-items-center rounded"
                                            style="height:40px; background-color: #ffea76;">
                                            <div class="col">
                                                <strong class="mb-0 custom-dark-text">Execution Time</strong>
                                            </div>
                                            <div class="col-auto custom-dark-text">
                                                <strong>
                                                    {{
                                                        getMillisecondsToHMS(histogramAudsumResult.statistics.execution_time_millis)
                                                    }}
                                                </strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="list-group-item"
                                        v-if="histogramAudsumResult?.statistics.queue_time_millis">
                                        <div class="row align-items-center">
                                            <div class="col">
                                                <span class="mb-0">Queue Time</span>
                                            </div>
                                            <div class="col-auto">
                                                <span>
                                                    {{
                                                        getMillisecondsToHMS(histogramAudsumResult.statistics.queue_time_millis)
                                                    }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="list-group-item"
                                        v-if="histogramAudsumResult?.statistics.data_scanned_bytes">
                                        <div class="row align-items-center">
                                            <div class="col">
                                                <span class="mb-0">Data Scanned</span>
                                            </div>
                                            <div class="col-auto">
                                                <span>
                                                    {{ bytesToKB(histogramAudsumResult.statistics.data_scanned_bytes) }}
                                                    KB
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="list-group-item"
                                        v-if="histogramAudsumResult?.statistics.submission_time">
                                        <div class="row align-items-center">
                                            <div class="col">
                                                <span class="mb-0">Submission Time</span>
                                            </div>
                                            <div class="col-auto">
                                                <span>
                                                    {{ histogramAudsumResult.statistics.submission_time }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="list-group-item"
                                        v-if="histogramAudsumResult?.statistics.completion_time">
                                        <div class="row align-items-center">
                                            <div class="col">
                                                <span class="mb-0">Completion Time</span>
                                            </div>
                                            <div class="col-auto">
                                                <span>
                                                    {{ histogramAudsumResult.statistics.completion_time }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="list-group-item" v-if="histogramAudsumResult?.latency">
                                        <div class="row align-items-center" style="height:40px">
                                            <div class="col">
                                                <span class="mb-0">Network Latency</span>
                                            </div>
                                            <div class="col-auto">
                                                <span>
                                                    {{ histogramAudsumResult.latency }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div v-if="!histogramAnalyticsLoading && !histogramAnalyticsResult">
                            <div class="card card-body">
                                <span class="text-center">No data found!</span>
                            </div>
                        </div>
                        <div v-else class="card">
                            <div class="card-header">
                                <div class="d-flex justify-content-center">
                                    <span class="fw-bold">Analytics Engine Execution Details</span>
                                </div>
                            </div>
                            <div class="card-body">
                                <div v-if="histogramAnalyticsLoading">
                                    <div class="d-flex justify-content-center align-items-center">
                                        <div class="spinner-border spinner-border-sm" role="status">
                                            <span class="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                    <div class="d-flex justify-content-center align-items-center">
                                        <p>{{ (timer / 1000).toFixed(3) }}s</p>
                                    </div>
                                </div>
                                <div class="list-group list-group-flush my-n3">
                                    <div class="list-group-item" v-if="histogramAnalyticsResult?.execution_id">
                                        <div class="row align-items-center">
                                            <div class="col">
                                                <span class="mb-0">Execution ID</span>
                                            </div>
                                            <div class="col-auto">
                                                <span>
                                                    {{ histogramAnalyticsResult.execution_id }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="list-group-item" v-if="histogramAnalyticsResult?.status">
                                        <div class="row align-items-center rounded" style="height: 40px;"
                                            :style="{ backgroundColor: getStatusColor(histogramAnalyticsResult.status) }">
                                            <div class="col">
                                                <span class="mb-0">Status</span>
                                            </div>
                                            <div class="col-auto">
                                                <strong class="text-uppercase">
                                                    <div class="spinner-border spinner-border-sm" role="status"
                                                        v-if="histogramAnalyticsResult.status.toLowerCase() == 'running'">
                                                        <span class="visually-hidden">Loading...</span>
                                                    </div>
                                                    {{ histogramAnalyticsResult.status }}
                                                </strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="list-group-item"
                                        v-if="histogramAnalyticsResult?.statistics.execution_time_millis">
                                        <div class="row align-items-center rounded"
                                            style="height:40px; background-color: #ffea76;">
                                            <div class="col">
                                                <strong class="mb-0 custom-dark-text">Execution Time</strong>
                                            </div>
                                            <div class="col-auto custom-dark-text">
                                                <strong>
                                                    {{
                                                        getMillisecondsToHMS(histogramAnalyticsResult.statistics.execution_time_millis)
                                                    }}
                                                </strong>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="list-group-item"
                                        v-if="histogramAnalyticsResult?.statistics.queue_time_millis">
                                        <div class="row align-items-center">
                                            <div class="col">
                                                <span class="mb-0">Queue Time</span>
                                            </div>
                                            <div class="col-auto">
                                                <span>
                                                    {{
                                                        getMillisecondsToHMS(histogramAnalyticsResult.statistics.queue_time_millis)
                                                    }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="list-group-item"
                                        v-if="histogramAnalyticsResult?.statistics.data_scanned_bytes">
                                        <div class="row align-items-center">
                                            <div class="col">
                                                <span class="mb-0">Data Scanned</span>
                                            </div>
                                            <div class="col-auto">
                                                <span>
                                                    {{ bytesToKB(histogramAnalyticsResult.statistics.data_scanned_bytes)
                                                    }}
                                                    KB
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="list-group-item"
                                        v-if="histogramAnalyticsResult?.statistics.submission_time">
                                        <div class="row align-items-center">
                                            <div class="col">
                                                <span class="mb-0">Submission Time</span>
                                            </div>
                                            <div class="col-auto">
                                                <span>
                                                    {{ histogramAnalyticsResult.statistics.submission_time }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="list-group-item"
                                        v-if="histogramAnalyticsResult?.statistics.completion_time">
                                        <div class="row align-items-center">
                                            <div class="col">
                                                <span class="mb-0">Completion Time</span>
                                            </div>
                                            <div class="col-auto">
                                                <span>
                                                    {{ histogramAnalyticsResult.statistics.completion_time }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="list-group-item" v-if="histogramAnalyticsResult?.latency">
                                        <div class="row align-items-center" style="height:40px">
                                            <div class="col">
                                                <span class="mb-0">Network Latency</span>
                                            </div>
                                            <div class="col-auto">
                                                <span>
                                                    {{ histogramAnalyticsResult.latency }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template v-slot:results>
                <div v-if="histogramAnalyticsLoading">
                    <div class="card card-body d-flex justify-content-center align-items-center mt-3">
                        <div class="spinner-border spinner-border-sm" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
                <div v-else-if="histogramAnalyticsResult">
                    <div v-if="countsHistogramMap" class="card card-body mt-3">
                        <div class="row">
                            <div v-for="(value, key) in countsHistogramMap" :key="key" class="col-12 p-2">
                                <Bar :data="value" :options="chartOptions" />
                            </div>
                        </div>
                    </div>
                    <div v-if="isDataViz">
                        <div class="card card-body mt-3">
                            <div class="row">
                                <div v-for="(value, key) in histogramMap" :key="key" class="col-6 p-2">
                                    <Bar :data="value" :options="chartOptions" />
                                </div>
                            </div>
                        </div>
                        <div class="card card-body">
                            <div class="row">
                                <div v-for="(value, key) in histogramCombMap" :key="key" class="col-12 p-2">
                                    <Bar :data="value" :options="chartOptions" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="card card-body mt-3">
                        <div class="row">
                            <div v-for="(value, key) in histogramMap" :key="key" class="col-6 p-2">
                                <Bar :data="value" :options="chartOptions" />
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else>
                    <div class="card card-body mt-3">
                        <span class="text-center">No data found!</span>
                    </div>
                </div>
            </template>
        </Tabs>
    </div>
</template>

<script>
import BasicHeader from '../../components/BasicHeader.vue';
import InputField from '../../components/InputField.vue';
import Tabs from "../../components/Tabs.vue";
import { 
    findCriterionTypeFromAudience, 
    simpleCriterionObjectBuilder, 
    complexCriterionObjectBuilder, 
    SimplerCriterionToComplexCriterionConverter,
    histogramQueryBuilder
} from '../../utils/audienceCriterion';
import axios from 'axios';
import { mapActions, mapState } from "vuex";
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, Colors } from 'chart.js'
import { Bar } from 'vue-chartjs'
ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, Colors)
export default {
    components: { Bar, BasicHeader, InputField, Tabs },
    data() {
        return {
            loading: false,
            attrLoading: false,
            audience: null,
            audiences: null,
            audienceOptions: [],
            isDataViz: true,
            breakRecursion: false,
            histogramAudsumResult: null,
            histogramAnalyticsResult: null,
            histogramAudsumLoading: false,
            histogramAnalyticsLoading: false,
            attributes: null,
            attributesOptions: [],
            dataEnvironment: "aqfer",
            dataset: "aqfer_internal_audience",
            startTime: null,
            audsumEndTime: null,
            analyticsEndTime: null,
            timer: 0,  // milliseconds elapsed
            timerInterval: null,
            comparisonBarData: [],
            countsHistogramMap: {},
            histogramMap: {},
            histogramCombMap: {},
            tabItems: [
                {
                    name: "Statistics",
                    id: "id1",
                    slot: "stats"
                },
                {
                    name: "Results",
                    id: "id2",
                    slot: "results"
                }
            ],
            chartOptions: {
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                    colors: {
                        forceOverride: false
                    }
                }
            },

            skipCache: false,
            attributesMultiselect: true,
            isSimplifiedCriterion: true,
            simplifiedCriterionValues: null,
            audienceCriterion: null,
            finalCriterion: null,
        }
    },
    watch: {
        audience: {
            handler(newValue) {
                let ad = this.audiences.find(audience => audience.audience_name == newValue)
                this.dataEnvironment = ad ? ad.audsum_data_environment : null;
                this.dataset = ad ? ad.audsum_dataset : null;
                this.getDataset();
                this.handleCriterion(ad);
            },
            deep: true
        },
    },
    computed: {
        isResultObtained() {
            return this.histogramAnalyticsResult?.status.toLowerCase() == 'succeeded' && this.histogramAudsumResult?.status.toLowerCase() == 'succeeded';
        }
    },
    methods: {
        ...mapActions({
            getDatasetAttributes: "amdp/getDatasetSchema",
            histogramQuery: "amdp/createAudsumHistogramQuery",
            getExecution: "amdp/getAudsumExecution",
            showModal: "showModal",
        }),
        async handleCriterion(audience) {
            this.isSimplifiedCriterion = true;
            this.simplifiedCriterionValues = [
                {
                    "criterion": [
                        {
                            "column": null,
                            "values": []
                        }
                    ]
                }
            ];
            this.audienceCriterion = this.finalCriterion = null;
            const type = findCriterionTypeFromAudience(audience);

            if (type == "Simple") {
                this.simplifiedCriterionValues[0]["criterion"] = simpleCriterionObjectBuilder(JSON.parse(audience.audience_criterion_json))
                this.audienceCriterion = {
                    criterion: [],
                    operator: "OR",
                    isCriterionNegated: false,
                };
            }
            else if (type == "Complex" || type == "Aggregate") {
                this.simplifiedCriterionValues = [{ "criterion": [] }];
                this.isSimplifiedCriterion = false;
                this.audienceCriterion = complexCriterionObjectBuilder(JSON.parse(audience.audience_criterion_json))
            }
            if (audience.parent_audiences != "") {
                this.isSimplifiedCriterion = true;
                this.simplifiedCriterionValues = []
                let listParentAudiences = await this.fetchSimplifiedCriterionValues(audience.parent_audiences)
                listParentAudiences.forEach(parentAudience => {
                    if (parentAudience.isDeleted == false) {
                        this.simplifiedCriterionValues.push({
                            "criterion": parentAudience.criterion,
                            "audienceName": parentAudience.audienceName,
                            "createdAt": parentAudience.createdAt,
                            "updatedAt": parentAudience.updatedAt
                        })
                    }
                })
            }

            // assign final criterion for payload
            let payload;
            if (this.isSimplifiedCriterion) {
                let complexCriterion = SimplerCriterionToComplexCriterionConverter(this.simplifiedCriterionValues[0]["criterion"])
                payload = histogramQueryBuilder(complexCriterion, { histogramColumns: [] });
            }
            else {
                payload = histogramQueryBuilder(this.audienceCriterion, { histogramColumns: [] });
            }
            this.finalCriterion = payload.criterion;
        },
        async fetchSimplifiedCriterionValues(parentAudiences) {
            const audienceNames = parentAudiences.split(",");
            let result = []
            const promises = audienceNames.map(async audienceName => {

                let audience = this.audiences.find(audience => audience.audience_name == audienceName)
                if (audience == undefined) {
                    return {
                        "isDeleted": true,
                        "id": audienceName,
                    };
                }
                return {
                    "criterion": simpleCriterionObjectBuilder(JSON.parse(audience.audience_criterion_json)),
                    "audienceName": audience.audience_name,
                    "createdAt": audience.created_date,
                    "updatedAt": audience.updated_at,
                    "isDeleted": false
                };
            });
            result = await Promise.all(promises);
            return result;
        },
        async stopPolling() {
            this.breakRecursion = true;
            this.histogramAnalyticsResult = null;
            this.histogramAudsumResult = null;
        },
        async getDataset() {
            this.attributesOptions = [];
            this.attrLoading = true;
            await this.getDatasetAttributes({
                data_environment: this.dataEnvironment,
                dataset: this.dataset,
            }).then((response) => {
                let attr = response.data?.schema.columns;
                if (attr) {
                    this.attributesOptions = attr.map((attribute) => {
                        return {
                            label: attribute.name,
                            value: attribute.name,
                        }
                    })
                }
            }).catch(error => {
                let err = error.response?.data.error ? error.response.data.error : error.message;
                let toastoptions = {
                    message: err || "Something went wrong while fetching dataset schema. Please contact support.",
                    type: "error"
                }
                this.$store.dispatch('addToast', toastoptions);
            })
            this.attrLoading = false;
        },
        async compareResults(appendDataviz) {
            this.loading = true;
            this.breakRecursion = false;
            this.histogramAudsumResult = null;
            this.histogramAnalyticsResult = null;
            this.histogramMap = {};
            this.countsHistogramMap = {};
            this.startTimer();
            this.startTime = new Date();
            await Promise.all([
                (async () => {
                    try {
                        this.histogramAudsumLoading = true;
                        let queryId;
                        let attribute_names = this.attributesMultiselect ? this.attributes : this.attributesText.split(',').map(attr => attr.trim());
                        let payload = {
                            "attribute_names": attribute_names,
                        }

                        if (!appendDataviz) {
                            payload["criterion"] = this.finalCriterion;
                        }
                        await this.histogramQuery({
                            data_environment: this.dataEnvironment,
                            dataset: this.dataset + (appendDataviz ? "$dataviz" : ""),
                            payload: appendDataviz ? {} : payload,
                            query_engine: "athena",
                            skipCacheHeader: !!this.skipCache,
                        }).then((response) => {
                            queryId = response.data ? response.data.execution_id : null;
                        })
                        if (queryId && !this.breakRecursion) {
                            await this.getResults(appendDataviz, queryId, false, false);
                            this.histogramAudsumResult.latency = this.getMillisecondsToHMS((this.audsumEndTime - this.startTime) - (this.histogramAudsumResult.statistics.queue_time_millis + this.histogramAudsumResult.statistics.execution_time_millis))
                        }
                    } catch (error) {
                        let err = error.response?.data.error ? error.response.data.error : error.message;
                        let toastoptions = {
                            message: err || "Something went wrong while creating audsum query. Please contact support.",
                            type: "error"
                        }
                        this.$store.dispatch('addToast', toastoptions);
                    } finally {
                        this.histogramAudsumLoading = false;
                    }
                })(),
                (async () => {
                    try {
                        this.histogramAnalyticsLoading = true;
                        let queryId;
                        let attribute_names = this.attributesMultiselect ? this.attributes : this.attributesText.split(',').map(attr => attr.trim());
                        let payload = {
                            "attribute_names": attribute_names,
                        }

                        if (!appendDataviz) {
                            payload["criterion"] = this.finalCriterion;
                        }
                        await this.histogramQuery({
                            data_environment: this.dataEnvironment,
                            dataset: this.dataset + (appendDataviz ? "$dataviz" : ""),
                            payload: appendDataviz ? {} : payload,
                            query_engine: "analytics",
                            skipCacheHeader: !!this.skipCache,
                        }).then((response) => {
                            queryId = response.data ? response.data.execution_id : null;
                        })
                        if (queryId && !this.breakRecursion) {
                            await this.getResults(appendDataviz, queryId, false, true);
                            this.histogramAnalyticsResult.latency = this.getMillisecondsToHMS((this.analyticsEndTime - this.startTime) - (this.histogramAnalyticsResult.statistics.queue_time_millis + this.histogramAnalyticsResult.statistics.execution_time_millis))
                            this.isDataViz = appendDataviz || this.dataset.endsWith("$dataviz")
                            this.displayHistograms();
                        }
                    } catch (error) {
                        let err = error.response?.data.error ? error.response.data.error : error.message;
                        let toastoptions = {
                            message: err || "Something went wrong while creating analytics engine query. Please contact support.",
                            type: "error"
                        }
                        this.$store.dispatch('addToast', toastoptions);
                    } finally {
                        this.histogramAnalyticsLoading = false;
                    }
                })()
            ]);
            await this.displayComparisonBars();
            this.loading = false;
            this.stopTimer();
        },
        async getResults(appendDataviz, queryId, executionCompleted, isAnalyticsEngine) {
            if (executionCompleted || this.breakRecursion) return;

            let errorTxt;
            try {
                await this.getExecution({
                    data_environment: this.dataEnvironment,
                    dataset: this.dataset + (appendDataviz ? "$dataviz" : ""),
                    execution_id: queryId,
                }).then(response => {
                    if (isAnalyticsEngine) {
                        this.histogramAnalyticsResult = response.data
                        this.displayResults(this.histogramAnalyticsResult)
                    } else {
                        this.histogramAudsumResult = response.data
                        this.displayResults(this.histogramAudsumResult)
                    }
                })
                if (this.isExecutionCompleted(isAnalyticsEngine ? this.histogramAnalyticsResult : this.histogramAudsumResult)) {
                    executionCompleted = true;
                    if (isAnalyticsEngine) {
                        this.analyticsEndTime = new Date();
                    } else {
                        this.audsumEndTime = new Date();
                    }
                }

                // Wait 1 second, then recursively call and AWAIT the result
                await new Promise(resolve => setTimeout(resolve, 1000));
                await this.getResults(appendDataviz, queryId, executionCompleted, isAnalyticsEngine);
            } catch (error) {
                errorTxt = error.response?.data.error ? error.response.data.error : error.message
                let toastoptions = {
                    message: errorTxt || "Something went wrong while fetching query results. Please contact support.",
                    type: "error"
                }
                console.error("Error: ", errorTxt)
                this.$store.dispatch('addToast', toastoptions);
            }
        },
        isExecutionCompleted(result) {
            if (!result || typeof result !== 'object')
                return false;
            if (result.status.toLowerCase() == "succeeded" || result.status.toLowerCase() == "failed") return true;
            return false;
        },
        displayResults(result) {
            if (result.status.toLowerCase() == "succeeded") result.statusClass = "text-success";
            else if (result.status.toLowerCase() == "failed") result.statusClass = "text-danger";
            else if (result.status.toLowerCase() == "running") result.statusClass = "text-primary";
            else result.statusClass = "text-warning";
        },
        displayHistograms() {
            // Add validation
            if (!this.histogramAnalyticsResult || !this.histogramAnalyticsResult.data || !this.histogramAnalyticsResult.data.body) {
                console.error('No histogram data available');
                return;
            }

            // Sort body array to put rows with "-" first
            const sortedBody = [...this.histogramAnalyticsResult.data.body].sort((a, b) => {
                const aIsDash = a[0] === "-" && a[1] === "-";
                const bIsDash = b[0] === "-" && b[1] === "-";

                if (aIsDash && !bIsDash) return -1; // a comes first
                if (!aIsDash && bIsDash) return 1;  // b comes first
                return 0; // keep original order for others
            });

            let countHistogram = {} // for displaying total counts
            let countsHistogramMap = {};
            let histograms = {};    // 'key' -> attribute name, 'value' -> { attribute_value: count, ...}
            let histogramMap = {};  // 'key' -> attribute name, 'value' -> chartData
            let histogramComb = {};
            let histogramCombMap = {};
            let singleAttributes = ['education', 'ethnicity', 'children', 'marital_state', 'gender', 'age_range', 'income_range', 'media', 'state', 'interests'];

            // Process each row in the body
            for (const [attrName, attrValue, count] of sortedBody) {
                if (this.isDataViz) {
                    if (attrName != "-") {
                        if (singleAttributes.includes(attrName)) {
                            if (!histograms[attrName]) histograms[attrName] = {};
                            histograms[attrName][attrValue] = count;
                        } else {
                            if (!histogramComb[attrName]) histogramComb[attrName] = {};
                            histogramComb[attrName][attrValue] = count;
                        }

                    } else {
                        // total counts histogram
                        if (!countHistogram["count"]) countHistogram["count"] = {};
                        countHistogram["count"]["Total Counts"] = count;
                    }
                } else {
                    if (attrName != "-") {
                        if (!histograms[attrName]) histograms[attrName] = {};
                        histograms[attrName][attrValue] = count;
                    } else {
                        // total counts histogram
                        if (!countHistogram["count"]) countHistogram["count"] = {};
                        countHistogram["count"]["Total Counts"] = count;
                    }
                }
            }

            // Generate total counts histogram object
            const labels = Object.keys(countHistogram["count"]);
            const values = Object.values(countHistogram["count"]);
            let chartData = {
                labels: labels,
                datasets: [{
                    label: "Total Counts",
                    data: values,
                }]
            }
            countsHistogramMap["Total Counts"] = chartData;
            this.countsHistogramMap = countsHistogramMap;

            // Generate histogram object
            for (const key in histograms) {
                const labels = Object.keys(histograms[key]);
                const values = Object.values(histograms[key]);

                if (labels.length > 0) { // Only add if there's data
                    let chartData = {
                        labels: labels,
                        datasets: [{
                            label: key,
                            data: values,
                        }]
                    }
                    histogramMap[key] = chartData;
                }
            }

            if (this.isDataViz) {
                for (const key in histogramComb) {
                    const labels = Object.keys(histogramComb[key]);
                    const values = Object.values(histogramComb[key]);

                    if (labels.length > 0) { // Only add if there's data
                        let chartData = {
                            labels: labels,
                            datasets: [{
                                label: key,
                                data: values,
                            }]
                        }
                        histogramCombMap[key] = chartData;
                    }
                }
                this.histogramCombMap = histogramCombMap;
            }
            this.histogramMap = histogramMap;
        },
        async displayComparisonBars() {
            if (!this.isResultObtained) return;
            this.comparisonBarData = [];
            let data1 = {
                labels: ['Queue Time (s)'],
                datasets: [{
                    label: 'Athena',
                    data: [this.histogramAudsumResult.statistics.queue_time_millis / 1000 ?? 0],
                    backgroundColor: 'rgba(0, 128, 128, 0.6)',
                },
                {
                    label: 'Analytics Engine',
                    data: [this.histogramAnalyticsResult.statistics.queue_time_millis / 1000 ?? 0],
                    backgroundColor: 'rgba(0, 161, 241, 0.5)',
                }]
            }
            let data2 = {
                labels: ['Execution Time (s)'],
                datasets: [{
                    label: 'Athena',
                    data: [this.histogramAudsumResult.statistics.execution_time_millis / 1000 ?? 0],
                    backgroundColor: 'rgba(0, 128, 128, 0.6)',
                },
                {
                    label: 'Analytics Engine',
                    data: [this.histogramAnalyticsResult.statistics.execution_time_millis / 1000 ?? 0],
                    backgroundColor: 'rgba(0, 161, 241, 0.5)',
                }]
            }
            this.comparisonBarData = [data1, data2];
        },
        getMillisecondsToHMS(ms) {
            ms = Number(ms);
            if (!ms || ms < 0) return '0s';

            const totalSeconds = ms / 1000;
            const h = Math.floor(totalSeconds / 3600);
            const m = Math.floor((totalSeconds % 3600) / 60);
            const s = (totalSeconds % 60).toFixed(3);  // keep 3 decimals

            const hDisplay = h > 0 ? `${h}h ` : '';
            const mDisplay = m > 0 ? `${m}m ` : '';
            const sDisplay = `${s}s`;

            return (hDisplay + mDisplay + sDisplay).trim();
        },
        bytesToKB(bytes) {
            if (typeof bytes !== 'number' || isNaN(bytes)) {
                return 0;
            }
            return (bytes / 1024).toFixed(2);
        },
        startTimer() {
            const startTime = performance.now();
            const update = (now) => {
                this.timer = now - startTime;
                if (this.loading) {
                    this.timerInterval = requestAnimationFrame(update);
                }
            };
            this.timerInterval = requestAnimationFrame(update);
        },
        stopTimer() {
            if (this.timerInterval) {
                cancelAnimationFrame(this.timerInterval);
                this.timerInterval = null;
            }
        },
        getStatusColor(status) {
            switch ((status || '').toLowerCase()) {
                case 'succeeded':
                    return 'rgba(0, 200, 83, 0.6)'; // green-ish
                case 'failed':
                    return 'rgba(244, 67, 54, 0.4)'; // red-ish
                case 'running':
                    return 'rgba(0, 161, 241, 0.5)'; // blue-ish
                default:
                    return 'transparent'; // transparent
            }
        },
        viewCriterion() {
            let ad = this.audiences.find(audience => audience.audience_name == this.audience)
            this.showModal({ component: "ViewCriterion", data: { criterion: this.finalCriterion, audience: ad, screensize: 'lg' } }); 
        }
    },
    async beforeMount() {
        await axios.get(`${this.$store.state.surfaceUrl}/v2/cids/${this.$store.state.currentClient.cid}/audiences`, {
            headers: {
                Authorization: `Bearer ${this.$store.state.accessToken}`
            }
        }).then((response) => {
            if (response.data) {
                this.audiences = response.data;
                this.audienceOptions = response.data.map(audience => {
                    return {
                        label: audience.audience_name,
                        value: audience.audience_name,
                        data_environment: audience.audsum_data_environment,
                        dataset: audience.audsum_dataset
                    }
                })
            }
        }).catch((error) => {
            let errorTxt = error.response?.data.error ? error.response.data.error : error.message
            let toastoptions = {
                message: errorTxt || "Something went wrong while fetching audience list. Please contact support.",
                type: "error"
            }
            console.error("Error: ", errorTxt)
            this.$store.dispatch('addToast', toastoptions);
        })
    },
    mounted() {
        this.compareResults(true);
    },
    beforeUnmount() {
        this.breakRecursion = true;
    }
}
</script>
<style scoped>
.breadcrumb-item::before {
    align-self: center;
    color: #d2ddec;
    font-family: Feather;
    font-size: 1.0rem;
}

::v-deep(.form-check-input) {
    background-color: #93bdf0 !important;
}

::v-deep(.form-check-input:checked) {
    background-color: #007bff !important;
}

::v-deep(.form-check-label span:first-child) {
    margin-right: 0.5rem;
}

/* Attributes input field - remove top-right and bottom-right border radius */
.attributes-input-wrapper ::v-deep(.multiselect),
.attributes-input-wrapper ::v-deep(.form-control[type="text"]) {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
}

/* Input group buttons - remove top-left and bottom-left border radius */
.attributes-buttons .btn {
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
}
</style>

<style lang="scss" scoped>
@use '../../theme/scss/_user-variables.scss' as *;

body.dark-mode {
    .custom-dark-text {
        color: $dark-elevated-bg !important;
    }
}
</style>